import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiArrowUpRight } from 'react-icons/fi';
import type { BlogPost } from '@/utils/blog';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

type BlogCardProps = {
  post: BlogPost;
  featured?: boolean;
  onClick: () => void;
  index: number;
  inView: boolean;
};

export default function BlogCard({ post, featured, onClick, index, inView }: BlogCardProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const postDate = new Date(post.date);
  const formattedDate = `${postDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })} ${postDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

  const initial = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 };
  const animate = prefersReducedMotion
    ? { opacity: inView ? 1 : 0 }
    : { opacity: inView ? 1 : 0, y: inView ? 0 : 24 };
  const transition = {
    duration: prefersReducedMotion ? 0.3 : 0.8,
    ease: [0.16, 1, 0.3, 1] as const,
    delay: index * 0.1,
  };

  if (featured) {
    return (
      <motion.article
        className="blog-featured group cursor-pointer"
        initial={initial}
        animate={animate}
        transition={transition}
        onClick={onClick}
      >
        <div className="blog-featured-media">
          {post.coverImage ? (
            <img
              src={post.coverImage}
              alt={post.title}
              loading="lazy"
              className="blog-featured-image"
            />
          ) : (
            <div className="blog-featured-image blog-no-image">
              <span>{t('portfolio.noImage')}</span>
            </div>
          )}
        </div>
        <div className="blog-featured-content">
          <time className="blog-meta" dateTime={post.date}>
            {formattedDate}
          </time>
          <h3 className="blog-featured-title">{post.title}</h3>
          <p className="blog-featured-excerpt">{post.excerpt}</p>
          <ul className="blog-tags">
            {post.tags.map((tag) => (
              <li key={tag}>{tag}</li>
            ))}
          </ul>
          <span className="blog-read-more">
            {t('blog.readMore')}
            <FiArrowUpRight className="blog-arrow" aria-hidden="true" />
          </span>
        </div>
      </motion.article>
    );
  }

  return (
    <motion.article
      className="blog-row group cursor-pointer"
      initial={initial}
      animate={animate}
      transition={transition}
      onClick={onClick}
    >
      <div className="blog-row-media">
        {post.coverImage ? (
          <img
            src={post.coverImage}
            alt={post.title}
            loading="lazy"
            className="blog-row-image"
          />
        ) : (
          <div className="blog-row-image blog-no-image">
            <span>{t('portfolio.noImage')}</span>
          </div>
        )}
      </div>
      <div className="blog-row-body">
        <time className="blog-row-date" dateTime={post.date}>
          {formattedDate}
        </time>
        <h3 className="blog-row-title">{post.title}</h3>
        <p className="blog-row-excerpt">{post.excerpt}</p>
        <ul className="blog-tags">
          {post.tags.map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
      </div>
      <FiArrowUpRight className="blog-row-arrow" aria-hidden="true" />
    </motion.article>
  );
}
